// JSON/YAML structural diff. Parses both sides (JSON first, YAML as a superset
// fallback), runs the shared record-set diff, and — per spec — degrades to the
// text engine if parsing fails. Key order is inherently ignored (paths, not
// positions). Emits a normalized-order path ledger for the side-by-side render.
import { expandJson } from './expand-json.js';
import { structuredSource } from './structured-source.js';
import { load as loadYaml } from 'js-yaml';
import { toRecordSet } from './records/model.js';
import { diffRecordSet } from './records/diff.js';
import { buildRecordsResult } from './records/records-result.js';
import { isArrayOfObjects, topLevelColumns, recordColumns, pickKeyColumn, hasDuplicateKeys } from './records/keys.js';
import { typeOf } from './records/flatten.js';
import { isIgnoredPath } from './records/path.js';
import { diffText } from './text-diff.js';
import { rawLineRows } from './line-diff.js';

export function diffJson(payload = {}) {
  const result = diffJsonCore(payload);
  if (!result.fallback) {
    try {
      result.render.source = structuredSource(payload.left || '', payload.right || '', result, payload.options);
    } catch {
      // Valid JSON can contain duplicate keys that the YAML location parser
      // rejects. Preserve the source and explicitly disclose missing markers.
      const rows = text => (text || '').split('\n').map((text, i) => ({ text: text.replace(/\r$/, ''), number: i + 1, marks: [], moves: [] }));
      result.render.source = { left: rows(payload.left), right: rows(payload.right), note: 'Source preserved; precise highlights and movement markers are unavailable for this syntax. See Unified for semantic changes.' };
    }
  }
  if (!result.fallback && result.format === 'json') {
    try {
      result.render.expandedSource = structuredSource(expandJson(payload.left || ''), expandJson(payload.right || ''), result, payload.options);
      result.render.expandedSource.note = 'JSON expanded for viewing. Line numbers refer to this expanded view; inputs are unchanged. Hatched gaps align absent or relocated content. Turn off Expand JSON to see original formatting.';
    } catch { /* YAML and unsupported source syntax retain their original view. */ }
  }
  return result;
}

function diffJsonCore(payload = {}) {
  const { left = '', right = '', format = 'json', options = {}, keyColumn } = payload;

  let lp;
  let rp;
  try {
    lp = parseStructured(left);
    rp = parseStructured(right);
  } catch (err) {
    const fallback = diffText(payload);
    fallback.fallback = { from: format, reason: err.message };
    return fallback;
  }

  const fmt = lp.kind === 'yaml' || rp.kind === 'yaml' ? 'yaml' : 'json';

  // Array of objects → match by KEY, exactly like CSV (the "rows matched by ID"
  // promise). Positional matching here was the bug: a re-sorted array reported
  // every field as changed. Only take this path when a key is found; otherwise
  // fall through to the positional path ledger (honest fallback).
  if (isArrayOfObjects(lp.value) && isArrayOfObjects(rp.value)) {
    const topCols = [...new Set([...topLevelColumns(lp.value), ...topLevelColumns(rp.value)])];
    const keyName = pickKeyColumn(topCols, keyColumn);
    if (keyName) {
      const leftCols = recordColumns(lp.value);
      const rightCols = recordColumns(rp.value);
      let lm = toRecordSet(lp.value, { keyColumns: [keyName] });
      let rm = toRecordSet(rp.value, { keyColumns: [keyName] });
      let warning = null;
      let keyCol = keyName;
      if (hasDuplicateKeys(lm) || hasDuplicateKeys(rm)) {
        lm = toRecordSet(lp.value, { keyColumns: [] });
        rm = toRecordSet(rp.value, { keyColumns: [] });
        warning = { kind: 'duplicate-key', key: keyName };
        keyCol = null;
      }
      return buildRecordsResult({ lm, rm, options, leftCols, rightCols, keyCol, format: fmt, rawRows: rawLineRows(left, right, options), warning });
    }
  }

  const lm = toRecordSet(lp.value, options);
  const rm = toRecordSet(rp.value, options);
  const res = diffRecordSet(lm, rm, options);
  const fc = res.fieldCounts;
  const ignore = new Set(options.ignoreFields || []);

  const changes = [];
  for (const m of res.matched) for (const c of m.fieldChanges) changes.push(c);
  for (const r of res.removed) changes.push({ kind: 'remove', path: r.key });
  for (const a of res.added) changes.push({ kind: 'add', path: a.key, newValue: '(record)' });
  if (keyOrderChanged(lp.value, rp.value)) changes.push({ kind: 'reorder' });

  // Path ledger for the side-by-side (single-document case; the common one).
  const rows =
    lm.records.length === 1 && rm.records.length === 1
      ? buildPathRows(lm.records[0].fields, rm.records[0].fields, res.matched[0]?.fieldChanges || [], ignore)
      : null;

  const rawRows = rawLineRows(left, right, options);

  return {
    format: fmt,
    added: fc.added,
    removed: fc.removed,
    modified: fc.modified,
    unchanged: fc.unchanged,
    typeChanges: fc.typeChanges,
    changes,
    render: { kind: 'structural', rows, changes, rawRows },
  };
}

// Sorted union of leaf paths, each tagged with its status and both values.
// Ignored paths are dropped from the ledger entirely.
function buildPathRows(lf, rf, fieldChanges, ignore = new Set()) {
  const changedByPath = new Map(fieldChanges.map((change) => [change.path, change]));
  const paths = [...new Set([...Object.keys(lf), ...Object.keys(rf)])].filter((p) => !isIgnoredPath(p, ignore)).sort();
  return paths.map((path) => {
    const inL = path in lf;
    const inR = path in rf;
    const change = changedByPath.get(path);
    if (inL && inR) {
      const ov = lf[path];
      const nv = rf[path];
      if (change?.kind === 'type') {
        return { path, status: 'type', oldValue: ov, newValue: nv, oldType: change.oldType, newType: change.newType };
      }
      if (change?.kind === 'change') return { path, status: 'change', oldValue: ov, newValue: nv };
      return { path, status: 'context', oldValue: ov, newValue: nv };
    }
    if (inL) return { path, status: 'remove', oldValue: lf[path] };
    return { path, status: 'add', newValue: rf[path] };
  });
}

function parseStructured(text) {
  if (!text.trim()) return { value: undefined, kind: 'empty' };
  try {
    return { value: JSON.parse(text), kind: 'json' };
  } catch {
    return { value: loadYaml(text), kind: 'yaml' };
  }
}

function keyOrderChanged(a, b, seen = new WeakSet()) {
  if (typeOf(a) === 'object' && typeOf(b) === 'object') {
    if (seen.has(a)) return false; // cyclic YAML alias graph — stop, don't overflow
    seen.add(a);
    const oa = Object.keys(a).filter((k) => k in b);
    const ob = Object.keys(b).filter((k) => k in a);
    if (oa.some((k, i) => k !== ob[i])) return true;
    for (const k of Object.keys(a)) if (k in b && keyOrderChanged(a[k], b[k], seen)) return true;
  }
  return false;
}
